// apps/admin/src/api/announcements.api.ts
//
// Phase 8.3 Wave 6 Task 45 — Admin Notification Center API client.
//
// Wraps three BE surfaces consumed by the Notification Center page:
//
//   1. Announcements (task-14) — CRUD `/api/v1/admin/announcements`
//      with i18n EN/VI/FR titles + bodies, target portals, priority, and
//      schedule window. Insert fires WS `system.announcement.new`
//       which the page subscribes to via wsClient.
//
//   2. Campaigns (task-14, schema-only — delivery deferred to Phase 9)
//      CRUD `/api/v1/admin/campaigns`. The Push / Email Blast / SMS tabs
//      filter the same collection by `channel`.
//
//   3. Anomaly events (task-26) — `GET /api/v1/admin/security/anomaly-events`
//      + `PATCH …/:id/dismiss`. Feeds the Incoming Alerts tab.
//
// Field-shape contracts match API-CONTRACTS.md sections 5 + 14 verbatim
// (snake_case wire). `apiClient` strips the success envelope, so list
// helpers capture the full envelope via a `transformResponse` sentinel
// (mirrors `exportCenter.api.ts` pattern).
//
// vg-binding: CONTEXT:decisions=P8.D-122,P8.D-137,P8.D-185,P8.D-211,P8.D-212,P8.D-147
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: API-CONTRACTS:announcements
// vg-binding: API-CONTRACTS:campaigns
// vg-binding: API-CONTRACTS:anomaly-events

import { apiClient, axiosClient } from '../lib/apiClient.js';

// ---------------------------------------------------------------------------
// Shared enums + i18n shape
// ---------------------------------------------------------------------------

export const ANNOUNCEMENT_PORTALS = [
  'admin',
  'merchant',
  'vendor',
  'homepage',
  'login',
] as const;
export type AnnouncementPortal = (typeof ANNOUNCEMENT_PORTALS)[number];

export const ANNOUNCEMENT_PRIORITIES = [
  'low',
  'medium',
  'high',
  'critical',
] as const;
export type AnnouncementPriority = (typeof ANNOUNCEMENT_PRIORITIES)[number];

/** Phase 8.3 amend #2 — category drives default styling tone. */
export const ANNOUNCEMENT_CATEGORIES = [
  'system_notice',
  'promo_marketing',
  'maintenance',
  'feature_release',
] as const;
export type AnnouncementCategory = (typeof ANNOUNCEMENT_CATEGORIES)[number];

/** Phase 8.3 amend #2 — slot placement inside each target portal. */
export const ANNOUNCEMENT_SURFACES = [
  'topbar',
  'page:orders',
  'page:dashboard',
  'page:products',
  'login',
  'homepage',
] as const;
export type AnnouncementSurface = (typeof ANNOUNCEMENT_SURFACES)[number];


export const ANNOUNCEMENT_STATUSES = [
  'draft',
  'scheduled',
  'live',
  'expired',
  'cancelled',
] as const;
export type AnnouncementStatus = (typeof ANNOUNCEMENT_STATUSES)[number];

export interface I18nString {
  en: string;
  vi?: string;
  fr?: string;
}

// ---------------------------------------------------------------------------
// Generic envelope-aware GET helper
// ---------------------------------------------------------------------------

interface EnvelopeShape<T> {
  ok: true;
  data: T;
  meta?: Record<string, unknown>;
}

/**
 * Issue a GET that captures the full success envelope (data + meta).
 * `apiClient.get` only returns the unwrapped `data`; this helper reads
 * the envelope before the response interceptor strips it.
 */
async function getEnvelope<T>(
  url: string,
): Promise<{ data: T; meta: Record<string, unknown> | undefined }> {
  const SENTINEL = Symbol.for('announcements.envelope');
  type Holder = { [k: symbol]: EnvelopeShape<T> | undefined };
  const holder: Holder = {};
  await axiosClient.request<T>({
    method: 'GET',
    url,
    transformResponse: [
      (raw: string) => {
        try {
          const parsed = JSON.parse(raw) as EnvelopeShape<T>;
          holder[SENTINEL] = parsed;
          return parsed;
        } catch {
          return raw;
        }
      },
    ],
  });
  const env = holder[SENTINEL];
  if (!env || env.ok !== true) {
    return { data: ([] as unknown) as T, meta: undefined };
  }
  return { data: env.data, meta: env.meta };
}

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

export interface AnnouncementWire {
  id: string;
  title_i18n: I18nString;
  body_i18n: I18nString;
  target_portals: AnnouncementPortal[];
  category: AnnouncementCategory;
  surface: AnnouncementSurface[];
  priority: AnnouncementPriority;
  schedule_start: string | null;
  schedule_end: string | null;
  dismissible: boolean;
  force_show: boolean;
  status: AnnouncementStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementInput {
  title_i18n: I18nString;
  body_i18n: I18nString;
  target_portals: AnnouncementPortal[];
  category: AnnouncementCategory;
  surface: AnnouncementSurface[];
  priority: AnnouncementPriority;
  schedule_start: string | null;
  schedule_end: string | null;
  dismissible: boolean;
  force_show: boolean;
}

export interface ListMeta {
  page: number;
  pageSize: number;
  total: number;
}

export interface ListAnnouncementsResult {
  rows: AnnouncementWire[];
  meta: ListMeta;
}

export interface ListAnnouncementsQuery {
  page?: number;
  pageSize?: number;
  status?: AnnouncementStatus | '';
  q?: string;
}

export async function listAnnouncements(
  q: ListAnnouncementsQuery = {},
): Promise<ListAnnouncementsResult> {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  if (q.q && q.q.length > 0) params.set('q', q.q);
  const qs = params.toString();
  const url = `/admin/announcements${qs ? `?${qs}` : ''}`;
  const { data, meta } = await getEnvelope<AnnouncementWire[]>(url);
  const safeRows = Array.isArray(data) ? data : [];
  const safeMeta: ListMeta = {
    page:
      typeof meta?.['page'] === 'number'
        ? (meta['page'] as number)
        : q.page ?? 1,
    pageSize:
      typeof meta?.['pageSize'] === 'number'
        ? (meta['pageSize'] as number)
        : q.pageSize ?? safeRows.length,
    total:
      typeof meta?.['total'] === 'number'
        ? (meta['total'] as number)
        : safeRows.length,
  };
  return { rows: safeRows, meta: safeMeta };
}

export async function createAnnouncement(
  input: AnnouncementInput,
): Promise<AnnouncementWire> {
  return apiClient.post<AnnouncementWire>('/admin/announcements', input);
}

// ---------------------------------------------------------------------------
// Round-18 (debug 2026-05-23) — Active-feed consumer surface.
//
// Calls the auth-only `/announcements/active?portal=<portal>` endpoint
// (no `announcement:write` gate). Returns currently published, in-window
// announcements targeted at the caller's portal, sorted critical→recent.
// ---------------------------------------------------------------------------

export async function listActiveAnnouncements(
  portal: AnnouncementPortal,
): Promise<AnnouncementWire[]> {
  const url = `/announcements/active?portal=${encodeURIComponent(portal)}`;
  const { data } = await getEnvelope<AnnouncementWire[]>(url);
  return Array.isArray(data) ? data : [];
}

export async function patchAnnouncement(
  id: string,
  input: Partial<AnnouncementInput>,
): Promise<AnnouncementWire> {
  return apiClient.patch<AnnouncementWire>(
    `/admin/announcements/${encodeURIComponent(id)}`,
    input,
  );
}

export async function deleteAnnouncement(
  id: string,
): Promise<{ id: string }> {
  return apiClient.delete<{ id: string }>(
    `/admin/announcements/${encodeURIComponent(id)}`,
  );
}

// ---------------------------------------------------------------------------
// Schedule-window client-side validation (mirrors BE 422 SCHEDULE_WINDOW_INVALID).
// Returns null on valid, message string on invalid.
//
// i18n: exposed as a factory `getValidateScheduleWindow(t)` so the bound
// translator (from the calling component's `useTranslation`) localises the
// messages. Falls back to the original VI copy when no translator is passed.
// ---------------------------------------------------------------------------

type Translate = (key: string, vars?: Record<string, unknown>) => string;

export type ValidateScheduleWindow = (
  startIso: string | null,
  endIso: string | null,
) => string | null;

export function getValidateScheduleWindow(t?: Translate): ValidateScheduleWindow {
  return (startIso, endIso) => {
    if (!startIso) {
      return t
        ? t('apiMessages.announcements.scheduleStartRequired')
        : 'schedule_start is required';
    }
    if (!endIso) return null;
    const start = Date.parse(startIso);
    const end = Date.parse(endIso);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return t
        ? t('apiMessages.announcements.scheduleDatesInvalid')
        : 'schedule_start / schedule_end is not a valid date';
    }
    if (end <= start) {
      return t
        ? t('apiMessages.announcements.scheduleEndAfterStart')
        : 'schedule_end must be after schedule_start';
    }
    return null;
  };
}

// ---------------------------------------------------------------------------
// Campaigns (push / email / sms — same collection, filter by channel)
// ---------------------------------------------------------------------------

export const CAMPAIGN_CHANNELS = ['push', 'email', 'sms'] as const;
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];

export const CAMPAIGN_STATUSES = [
  'draft',
  'scheduled',
  'sending',
  'done',
  'failed',
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export interface CampaignAudience {
  roles?: string[];
  merchant_tiers?: string[];
  vendor_ratings?: string[];
}

export interface CampaignTracking {
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;
}

export interface CampaignWire {
  id: string;
  name: string;
  channel: CampaignChannel;
  template_id: string;
  audience: CampaignAudience;
  schedule_at: string | null;
  status: CampaignStatus;
  tracking?: CampaignTracking;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignInput {
  name: string;
  channel: CampaignChannel;
  template_id: string;
  audience: CampaignAudience;
  schedule_at: string;
}

export interface ListCampaignsResult {
  rows: CampaignWire[];
  meta: ListMeta;
}

export interface ListCampaignsQuery {
  page?: number;
  pageSize?: number;
  channel?: CampaignChannel;
  status?: CampaignStatus;
}

export async function listCampaigns(
  q: ListCampaignsQuery = {},
): Promise<ListCampaignsResult> {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  // channel is applied client-side — BE has no per-channel query yet.
  const qs = params.toString();
  const url = `/admin/campaigns${qs ? `?${qs}` : ''}`;
  const { data, meta } = await getEnvelope<CampaignWire[]>(url);
  const all = Array.isArray(data) ? data : [];
  const filtered = q.channel ? all.filter((c) => c.channel === q.channel) : all;
  const safeMeta: ListMeta = {
    page:
      typeof meta?.['page'] === 'number'
        ? (meta['page'] as number)
        : q.page ?? 1,
    pageSize:
      typeof meta?.['pageSize'] === 'number'
        ? (meta['pageSize'] as number)
        : q.pageSize ?? filtered.length,
    total: filtered.length,
  };
  return { rows: filtered, meta: safeMeta };
}

export async function createCampaign(
  input: CampaignInput,
): Promise<CampaignWire> {
  return apiClient.post<CampaignWire>('/admin/campaigns', input);
}

export async function patchCampaign(
  id: string,
  input: Partial<CampaignInput>,
): Promise<CampaignWire> {
  return apiClient.patch<CampaignWire>(
    `/admin/campaigns/${encodeURIComponent(id)}`,
    input,
  );
}

export async function deleteCampaign(id: string): Promise<{ id: string }> {
  return apiClient.delete<{ id: string }>(
    `/admin/campaigns/${encodeURIComponent(id)}`,
  );
}

// ---------------------------------------------------------------------------
// Anomaly events (task-26 BE) — Incoming Alerts tab feed
// ---------------------------------------------------------------------------

export const ANOMALY_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type AnomalySeverity = (typeof ANOMALY_SEVERITIES)[number];

export interface AnomalyEventWire {
  id: string;
  rule_id: string;
  severity: AnomalySeverity;
  actor_user_id: string | null;
  details: Record<string, unknown>;
  occurred_at: string;
  dismissed_by: string | null;
  dismissed_at: string | null;
  audit_log_query_url: string;
}

export interface ListAnomalyQuery {
  rule_id?: string;
  severity?: AnomalySeverity;
  actor_user_id?: string;
  dismissed?: 'true' | 'false';
  date_from?: string;
  date_to?: string;
  cursor?: string;
  limit?: number;
}

export interface ListAnomalyResult {
  rows: AnomalyEventWire[];
  meta: { cursor_next: string | null };
}

export async function listAnomalyEvents(
  q: ListAnomalyQuery = {},
): Promise<ListAnomalyResult> {
  const params = new URLSearchParams();
  if (q.rule_id) params.set('rule_id', q.rule_id);
  if (q.severity) params.set('severity', q.severity);
  if (q.actor_user_id) params.set('actor_user_id', q.actor_user_id);
  if (q.dismissed) params.set('dismissed', q.dismissed);
  if (q.date_from) params.set('date_from', q.date_from);
  if (q.date_to) params.set('date_to', q.date_to);
  if (q.cursor) params.set('cursor', q.cursor);
  if (q.limit) params.set('limit', String(q.limit));
  const qs = params.toString();
  const url = `/admin/security/anomaly-events${qs ? `?${qs}` : ''}`;
  const { data, meta } = await getEnvelope<AnomalyEventWire[]>(url);
  const rows = Array.isArray(data) ? data : [];
  const cursor_next =
    meta && typeof meta['cursor_next'] === 'string'
      ? (meta['cursor_next'] as string)
      : null;
  return { rows, meta: { cursor_next } };
}

export async function dismissAnomalyEvent(
  id: string,
  reason: string,
): Promise<AnomalyEventWire> {
  return apiClient.patch<AnomalyEventWire>(
    `/admin/security/anomaly-events/${encodeURIComponent(id)}/dismiss`,
    { reason },
  );
}

// ---------------------------------------------------------------------------
// Email templates lookup (task-44 selector) — minimal shape; the editor
// page owns the full CRUD, this client only needs id + name + language to
// power the Email Blast tab's template picker.
// ---------------------------------------------------------------------------

export interface EmailTemplateOption {
  id: string;
  name: string;
  category: string | null;
  language: string;
}

export async function listEmailTemplateOptions(): Promise<
  EmailTemplateOption[]
> {
  try {
    const { data } = await getEnvelope<EmailTemplateOption[]>(
      '/admin/email-templates',
    );
    return Array.isArray(data) ? data : [];
  } catch {
    // Endpoint not shipped yet — return empty list so the tab still
    // renders + the user gets an inline notice instead of a hard error.
    return [];
  }
}
